import React, { useState } from 'react';
import { X, CheckCircle, Timer, Gauge, BarChart2, Star } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useHabitStore } from '../stores/habitStore';
import { useHabitSessionStore } from '../stores/habitSessionStore';
import { getStreakMultiplier, calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import { formatPoints } from '../utils/levelCalculation';
import type { Habit } from '../types';

interface HabitCompletionModalProps {
  habit: Habit;
  onClose: () => void;
  onModeSelected: (mode: 'quick' | 'timer' | 'stopwatch') => void;
}

export function HabitCompletionModal({ habit, onClose, onModeSelected }: HabitCompletionModalProps) {
  const { getCategoryColor, getCategoryName } = useCategories();
  const { userLevel } = useHabitStore();
  const [rememberChoice, setRememberChoice] = useState(false);

  const categoryColor = getCategoryColor(habit.category);
  const streakInfo = getStreakMultiplier(habit.streak);

  const HabitIcon = habit.icon && (Icons[habit.icon as keyof typeof Icons] as any)
    ? (Icons[habit.icon as keyof typeof Icons] as any)
    : Star;

  const baseXP = calculateLevelBasedPoints(POINTS.HABIT, userLevel);
  const quickCompleteXP = Math.round(baseXP * streakInfo.multiplier);

  const handleModeSelect = (mode: 'quick' | 'timer' | 'stopwatch') => {
    if (rememberChoice) {
      localStorage.setItem(`habit-${habit.id}-default-mode`, mode);
    }
    onModeSelected(mode);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full border border-border">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-lg"
                style={{ backgroundColor: `${categoryColor}15` }}
              >
                <HabitIcon
                  className="w-6 h-6"
                  style={{ color: categoryColor }}
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {habit.name}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {getCategoryName(habit.category)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-muted rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {habit.streak > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg mb-6"
              style={{ backgroundColor: `${categoryColor}10` }}
            >
              <BarChart2 className="w-4 h-4" style={{ color: categoryColor }} />
              <span className="text-sm font-medium" style={{ color: categoryColor }}>
                {habit.streak} day streak
              </span>
              {streakInfo.multiplier > 1 && (
                <span className="text-sm" style={{ color: categoryColor }}>
                  • {streakInfo.multiplier}x multiplier
                </span>
              )}
            </div>
          )}

          <p className="text-sm text-muted-foreground mb-6">
            How would you like to complete this habit?
          </p>

          <div className="space-y-3">
            <button
              onClick={() => handleModeSelect('quick')}
              className="w-full p-4 rounded-lg border-2 border-border hover:border-primary transition-all group text-left"
              style={{
                borderColor: 'var(--border)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = categoryColor;
                e.currentTarget.style.backgroundColor = `${categoryColor}05`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${categoryColor}15` }}
                >
                  <CheckCircle className="w-5 h-5" style={{ color: categoryColor }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">
                    Quick Complete
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Mark as done instantly
                  </p>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4" style={{ color: categoryColor }} />
                    <span className="text-sm font-semibold" style={{ color: categoryColor }}>
                      {formatPoints(quickCompleteXP)} XP
                    </span>
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleModeSelect('timer')}
              className="w-full p-4 rounded-lg border-2 border-border hover:border-primary transition-all group text-left"
              style={{
                borderColor: 'var(--border)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = categoryColor;
                e.currentTarget.style.backgroundColor = `${categoryColor}05`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${categoryColor}15` }}
                >
                  <Timer className="w-5 h-5" style={{ color: categoryColor }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">
                    Start Timer
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Set a countdown timer
                  </p>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4" style={{ color: categoryColor }} />
                    <span className="text-sm font-semibold" style={{ color: categoryColor }}>
                      {formatPoints(quickCompleteXP)} XP + 5 XP/min
                    </span>
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleModeSelect('stopwatch')}
              className="w-full p-4 rounded-lg border-2 border-border hover:border-primary transition-all group text-left"
              style={{
                borderColor: 'var(--border)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = categoryColor;
                e.currentTarget.style.backgroundColor = `${categoryColor}05`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${categoryColor}15` }}
                >
                  <Gauge className="w-5 h-5" style={{ color: categoryColor }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">
                    Start Stopwatch
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Track time as you go
                  </p>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4" style={{ color: categoryColor }} />
                    <span className="text-sm font-semibold" style={{ color: categoryColor }}>
                      {formatPoints(quickCompleteXP)} XP + 5 XP/min
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberChoice}
                onChange={(e) => setRememberChoice(e.target.checked)}
                className="rounded border-input"
              />
              <span className="text-sm text-muted-foreground">
                Remember my choice for this habit
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
